import { FormEventHandler, useEffect, useRef, useState } from "react";
import { Col, Form, InputGroup, Row, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { FaCheck } from "react-icons/fa";
import { useParams } from "react-router-dom";
import { Item as ItemModel } from "../../../models/item";
import * as ItemsApi from "../../../network/items_api";
import { ItemInput } from "../../../network/items_api";
import ItemInputField from "../../../components/FormFields/ItemInputField";
import Item from "./Item";
import { useTranslation } from "react-i18next";
import ActionButton from "../../../components/Buttons/Action/ActionButton";

interface AddEditGameSetItemsProps {
  itemToEdit?: ItemModel;
  onItemSaved: (item: ItemModel) => void;
  className?: string;
}

const AddEditGameSetItems = ({
  itemToEdit,
  onItemSaved,
  className,
}: AddEditGameSetItemsProps) => {
  const gameSetId = useParams();
  const [file, setFile] = useState<File | null>(null);
  const [separator, setSeparator] = useState(";");
  const [items, setItems] = useState<ItemModel[]>([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [showItemsLoadingError, setShowItemsLoadingError] = useState(false);
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ItemInput>({
    defaultValues: {
      question: "",
      solution: "",
    },
  });
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  /**
   * Creates a new item based on the input
   * @param {ItemInput} input - The item to be created
   */
  async function onItemSubmit(input: ItemInput) {
    try {
      let itemResponse: ItemModel;
      if (itemToEdit) {
        itemResponse = await ItemsApi.updateItem(
          itemToEdit._id,
          input,
          gameSetId.gamesetId!.toString()
        );
      } else {
        itemResponse = await ItemsApi.createItem(
          input,
          gameSetId.gamesetId!.toString()
        );
      }
      onItemSaved(itemResponse);
      setItems([...items, itemResponse]);
    } catch (error) {
      console.error(error);
      alert(error);
    }
  }

  useEffect(() => {
    loadItems();
  }, []);

  /**
   * Loads the items from the database
   */
  async function loadItems() {
    try {
      setShowItemsLoadingError(false);
      setItemsLoading(true);
      const items = await ItemsApi.fetchItems(gameSetId.gamesetId!.toString());
      setItems(items);
    } catch (error) {
      console.error(error);
      alert(error);
      setShowItemsLoadingError(true);
    } finally {
      setItemsLoading(false);
    }
  }

  /**
   * Deletes an item from the databasa
   * @param {ItemModel} item - The item to be deleted
   */
  async function deleteItem(item: ItemModel) {
    try {
      await ItemsApi.deleteItem(item._id, gameSetId.gamesetId!.toString());
      setItems(items.filter((existingItem) => existingItem._id !== item._id));
    } catch (error) {
      console.error(error);
      alert(error);
    }
  }

  /**
   * Handles the file change based on the upload
   * @param {ChangeEvent} event - The event object representing the file change
   */
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (uploadedFile) {
      if (
        uploadedFile.type === "text/plain" ||
        uploadedFile.type === "text/csv"
      ) {
        setFile(uploadedFile);
      } else {
        console.error(`${t("error.wrongFormat")}`);
        alert(`${t("error.wrongFormat")}`);
      }
    }
  };

  /**
   * Handles the submission of the upload by calling a function to read the content of the file and after that calling a function to delete it
   * @param {FormEventHandler} event - The event object representing the form submission
   */
  const handleFileUploadSubmit: FormEventHandler<HTMLFormElement> = async (
    event
  ) => {
    event.preventDefault();
    try {
      await readLinesFromFileAsync(file!);
      // Deleting the file after reading
      deleteFile(file!);
    } catch (error) {
      console.error(`${t("error.readingFile")}`, error);
      alert(`${t("error.readingFile")}` + error);
    }
  };

  /**
   * Reads the lines from the file and splits them to question and solution. Creates a new item and reloads the items.
   * @param {File} file File to be read
   * @returns
   */
  const readLinesFromFileAsync = (file: File): Promise<void> => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
  
      fileReader.onload = async () => {
        try {
          const fileContent = fileReader.result as string;
          const linesArray = fileContent.split("\n");
  
          const creationPromises = linesArray.map((line) => {
            if (line.trim() !== "" && line.includes(separator)) {
              const [question, solution] = line.split(separator);
              const newItem: ItemModel = {
                _id: "",
                question: question.trim(),
                solution: solution.trim(),
              };
              return onItemSubmit(newItem);
            }
          });
  
          await Promise.all(creationPromises);
          await loadItems();
          resolve();
        } catch (err) {
          reject(err);
        }
      };
  
      fileReader.onerror = () => {
        reject(new Error(`${t("error.failedReading")}`));
      };
  
      fileReader.readAsText(file);
    });
  };
  

  /**
   * Deletes a file
   * @param {File} file File to be deleted
   */
  const deleteFile = (file: File) => {
    try {
      setFile(null); // Clear the file state
    } catch (error) {
      console.error(`${t("error.deletingFile")}`, error);
      alert(`${t("error.deletingFile")}` + error);
    }
  };

  /**
   * Sets the changed value as the separator
   * @param {ChangeEvent} event - The event object representing the separator change
   */
  const handleSeparatorChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSeparator(event.target.value);
  };

  /**
   * Retrieves the current date and time as a formatted string
   * @returns {string} A string representing the current date and time in the format 'YYYYMMDDHHMMSS'
   */
  const retrieveCurrentDateTimeString = () => {
    const now: Date = new Date();

    const year: string = now.getFullYear().toString();
    const month: string = (now.getMonth() + 1).toString().padStart(2, "0");
    const day: string = now.getDate().toString().padStart(2, "0");
    const hours: string = now.getHours().toString().padStart(2, "0");
    const minutes: string = now.getMinutes().toString().padStart(2, "0");
    const seconds: string = now.getSeconds().toString().padStart(2, "0");

    return year + month + day + hours + minutes + seconds;
  };

  /**
   * Generates CSV content based on the provided items from database.
   * @returns {Blob} A Blob object representing the CSV content.
   */
  const generateCSVContent = () => {
    let csvContent = "";

    items.forEach((item) => {
      csvContent += `${item.question};${item.solution}\n`;
    });

    return new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  };

  /**
   * Handles the download of a CSV file containing data generated by the generateCSVContent function
   */
  const handleDownload = () => {
    const csvBlob = generateCSVContent();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(csvBlob);
    link.setAttribute(
      "download",
      `solvize-gameset-${retrieveCurrentDateTimeString()}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const itemsForm = (
    <>
      <h2 className={"lightText"}>{t("gameSet.items")}</h2>
      <h3 className={"lightText"}>{t("gameSet.upload")}</h3>
      <Row></Row>
      <Form id="FileUploadForm" onSubmit={handleFileUploadSubmit}>
        <Row className={`${"mb-5"} ${"flexCenter"}`}>
          {!file && (
            <Col
              md={2}
              className="d-flex align-items-center justify-content-center"
            >
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <ActionButton onClick={() => fileInputRef.current?.click()}>
                {t("gameSet.chooseFile")}
              </ActionButton>
            </Col>
          )}

          {file && (
            <>
              <Col
                md={2}
                className="d-flex align-items-center justify-content-center"
              >
                <span className="lightText text-truncate" title={file.name}>
                  {file.name}
                </span>
              </Col>
              <Col md={2}>
                <InputGroup>
                  <InputGroup.Text>{t("gameSet.separator")}</InputGroup.Text>
                  <Form.Control
                    name="separator"
                    id="separator"
                    type="text"
                    placeholder={separator}
                    value={separator}
                    onChange={handleSeparatorChange}
                  />
                </InputGroup>
              </Col>
              <Col md={2}>
                <ActionButton type="submit" form="FileUploadForm">
                  {t("common.add")}
                </ActionButton>
              </Col>
              <Col md={2}>
                <ActionButton onClick={() => setFile(null)}>
                  {t("common.cancel")}
                </ActionButton>
              </Col>
            </>
          )}
        </Row>
      </Form>
      <h3 className={"lightText"}>{t("gameSet.addNewItem")}</h3>
      <Row></Row>
      <Form id="AddEditGameSetItemsForm" onSubmit={handleSubmit(onItemSubmit)}>
        <Row className={`${"mb-5"} ${"flexCenter"}`}>
          <Col md={5}>
            <ItemInputField
              name="question"
              label={t("gameSet.question")}
              type="text"
              placeholder={t("gameSet.question")}
              register={register}
              registerOptions={{ required: "Required" }}
              error={errors.question}
            />
          </Col>
          <Col md={5}>
            <ItemInputField
              name="solution"
              label={t("gameSet.solution")}
              type="text"
              placeholder={t("gameSet.solution")}
              register={register}
              registerOptions={{ required: "Required" }}
              error={errors.solution}
            />
          </Col>
          <Col md={1}>
            <ActionButton
              type="submit"
              form="AddEditGameSetItemsForm"
              disabled={isSubmitting}
            >
              <FaCheck />
            </ActionButton>
          </Col>
        </Row>
      </Form>
      <h3 className={"lightText"}>{t("gameSet.itemsInSet")}</h3>
      {items.map((item) => (
        <Item item={item} key={item._id} onDeleteItemClicked={deleteItem} />
      ))}
      <div className={`${"button-container"} ${"flexCenter"}`}>
        <ActionButton onClick={handleDownload}>
          {t("gameSet.export")}
        </ActionButton>
      </div>
    </>
  );

  return (
    <>
      {itemsLoading && (
        <Spinner className="custom-spinner" animation="border" />
      )}
      {showItemsLoadingError && <p>{t("error.refresh")}</p>}
      {!itemsLoading && !showItemsLoadingError && <>{itemsForm}</>}
    </>
  );
};

export default AddEditGameSetItems;
